import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { fetchAuthSession } from "aws-amplify/auth";
import { DataTypes, User, UsersParams, UsersPagination, PaginatedUsersResponse } from "../../models/users";
import { NewUserForm } from "../../models/forms";
import { mapUser } from "../../utils/mappers";
import { isObjectRecord } from "../../utils/functions/api-errors";

type ApiRecord = Record<string, unknown>;
type UserApiRecord = ApiRecord;
type GoogleUserRegistrationRequest = Record<string, unknown>;
type CreateFictionalUserRequest = Pick<User, "email" | "firstName" | "lastName">;

type DeviceRegistrationRequest = {
  userId: User["id"];
  deviceData: Record<string, unknown>;
};

type UserDevice = Record<string, unknown>;
type SavedUserEvent = Record<string, unknown>;

type BulkUserStatusRequest = {
  userIds: User["id"][];
};

type BulkUserBanRequest = BulkUserStatusRequest & {
  banReason?: string;
};

const getResponseData = (response: unknown): unknown => {
  if (isObjectRecord(response) && "data" in response) return response.data;
  return response;
};

const isApiRecordArray = (value: unknown): value is ApiRecord[] =>
  Array.isArray(value) && value.every(isObjectRecord);

const getRecordArrayResponse = (response: unknown): ApiRecord[] => {
  const data = getResponseData(response);
  if (isObjectRecord(data) && isApiRecordArray(data.items)) return data.items;
  return isApiRecordArray(data) ? data : [];
};

const getTypedArrayResponse = <T>(
  response: unknown,
  isItem: (value: unknown) => value is T
): T[] => {
  const data = getResponseData(response);
  if (Array.isArray(data)) return data.filter(isItem);
  if (isObjectRecord(data) && Array.isArray(data.items)) {
    return data.items.filter(isItem);
  }

  return [];
};

const getRecordResponse = (response: unknown): ApiRecord | undefined => {
  const data = getResponseData(response);

  if (isObjectRecord(data)) return data;
  if (Array.isArray(data)) return data.find(isObjectRecord);

  return undefined;
};

const isUserType = (value: unknown): value is DataTypes =>
  isObjectRecord(value) &&
  typeof value.id === "number" &&
  typeof value.name === "string";

const getUserTypesResponse = (response: unknown): DataTypes[] => {
  const data = getResponseData(response);

  if (Array.isArray(data)) return data.filter(isUserType);
  if (isObjectRecord(data) && Array.isArray(data.userTypes)) {
    return data.userTypes.filter(isUserType);
  }

  return [];
};

const isUserDevice = (value: unknown): value is UserDevice => isObjectRecord(value);
const isSavedUserEvent = (value: unknown): value is SavedUserEvent => isObjectRecord(value);

const hasBanAuditState = (user: UserApiRecord): boolean =>
  Boolean(user.banned) ||
  user.banStatus === "REVERSIBLE" ||
  user.ban_status === "REVERSIBLE";

const userTag = (id: string | number) => ({
  type: "users" as const,
  id: String(id),
});
const userListTag = { type: "users" as const, id: "LIST" };
const userSubTag = (id: string | number, suffix: string) => ({
  type: "users" as const,
  id: `${String(id)}-${suffix}`,
});

/** Omite filtros de texto vacíos para evitar errores de validación del backend. */
export const normalizeUsersQueryArgs = (
  params: UsersParams = {}
): UsersParams => {
  const normalized: UsersParams = {};

  if (params.id != null) normalized.id = params.id;
  if (params.userTypeId != null) normalized.userTypeId = params.userTypeId;
  if (params.tenantId !== undefined) normalized.tenantId = params.tenantId;
  if (params.banned !== undefined) normalized.banned = params.banned;
  if (params.limit != null) normalized.limit = params.limit;
  if (params.page != null) normalized.page = params.page;

  if (params.email?.trim()) normalized.email = params.email.trim();
  if (params.firstName?.trim()) normalized.firstName = params.firstName.trim();
  if (params.lastName?.trim()) normalized.lastName = params.lastName.trim();

  return normalized;
};

// Use the same auth configuration as the main API
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API,
  prepareHeaders: async (headers) => {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString() ?? "";

    // Set default content type for requests with body
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    if (!!accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    return headers;
  },
});

export const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: baseQuery,
  refetchOnMountOrArgChange: true,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: ["users"],
  endpoints: (build) => ({
    // Queries - Backoffice endpoints
    getUsers: build.query<PaginatedUsersResponse, UsersParams | void>({
      query: (params) => ({
        url: "/users",
        params: normalizeUsersQueryArgs(params || {}),
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const normalized = normalizeUsersQueryArgs(queryArgs || {});
        return `${endpointName}(${JSON.stringify(normalized)})`;
      },
      transformResponse: (response: unknown) => {
        const rawData = getResponseData(response);
        const users = getRecordArrayResponse(response);
        const mappedUsers = users.map(mapUser);

        const pagination: UsersPagination | undefined =
          isObjectRecord(rawData) && isObjectRecord(rawData.pagination)
            ? {
                page: Number(rawData.pagination.page ?? 1),
                limit: Number(rawData.pagination.limit ?? 25),
                total: Number(rawData.pagination.total ?? mappedUsers.length),
                totalPages: Number(rawData.pagination.totalPages ?? 1),
              }
            : undefined;

        if (process.env.NODE_ENV !== "production") {
          const banAuditRows = users
            .filter(hasBanAuditState)
            .slice(0, 20)
            .map((user) => ({
              id: user.id,
              email: user.email,
              banned: user.banned,
              banStatus: user.banStatus,
              ban_status: user.ban_status,
              bannedBy: user.bannedBy,
              banned_by: user.banned_by,
              banReason: user.banReason,
              ban_reason: user.ban_reason,
            }));

          console.info("[usersApi/getUsers] ban audit payload", banAuditRows);
        }

        return { users: mappedUsers, pagination };
      },
      providesTags: (result) =>
        result?.users
          ? [
              ...result.users.map(({ id }) => userTag(id)),
              userListTag,
            ]
          : [userListTag],
    }),
    
    getUserById: build.query<User, string | number>({
      query: (id) => ({
        url: `/users/${id}`,
      }),
      transformResponse: (response: unknown) => {
        return mapUser(getRecordResponse(response));
      },
      providesTags: (_result, _error, id) => [userTag(id)],
    }),
    
    getUserTypes: build.query<DataTypes[], void>({
      query: () => "/users/types",
      transformResponse: (response: unknown) => {
        return getUserTypesResponse(response);
      },
      providesTags: [{ type: "users", id: "TYPES" }],
    }),

    // Mutations
    registerUser: build.mutation<User, NewUserForm>({
      query: (data) => ({
        url: "/users/register",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [userListTag],
    }),
    
    registerGoogleUser: build.mutation<User, GoogleUserRegistrationRequest>({
      query: (data) => ({
        url: "/users/register/google",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [userListTag],
    }),
    
    createFictionalUser: build.mutation<User, CreateFictionalUserRequest>({
      query: (data) => ({
        url: "/users/fictional",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [userListTag],
    }),

    updateUser: build.mutation<User, Partial<User> & { id: number }>({
      query: (data) => ({
        url: `/users/${data.id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, data) => [userTag(data.id), userListTag],
    }),

    banUsers: build.mutation<void, BulkUserBanRequest>({
      query: (data) => ({
        url: "/users/ban",
        method: "PATCH",
        body: {
          userIds: data.userIds,
          ...(data.banReason !== undefined && { banReason: data.banReason }),
        },
      }),
      invalidatesTags: (_result, _error, { userIds }) => [
        userListTag,
        ...userIds.map((id) => userTag(id)),
      ],
    }),

    unbanUsers: build.mutation<void, BulkUserStatusRequest>({
      query: (data) => ({
        url: "/users/unban",
        method: "PATCH",
        body: {
          userIds: data.userIds,
        },
      }),
      invalidatesTags: (_result, _error, { userIds }) => [
        userListTag,
        ...userIds.map((id) => userTag(id)),
      ],
    }),

    getUserByEmail: build.query<User, string>({
      query: (email) => ({
        url: `/users/${encodeURIComponent(email)}/email`,
      }),
      transformResponse: (response: unknown) => {
        return mapUser(getRecordResponse(response));
      },
      providesTags: (result) => (result ? [userTag(result.id)] : []),
    }),

    // Device management
    registerDevice: build.mutation<void, DeviceRegistrationRequest>({
      query: ({ userId, deviceData }) => ({
        url: `/users/${userId}/devices/register`,
        method: "POST",
        body: deviceData,
      }),
      invalidatesTags: (_result, _error, { userId }) => [userSubTag(userId, "devices")],
    }),
    
    getUserDevices: build.query<UserDevice[], number>({
      query: (userId) => `/users/${userId}/devices`,
      transformResponse: (response: unknown) => {
        return getTypedArrayResponse(response, isUserDevice);
      },
      providesTags: (_result, _error, userId) => [userSubTag(userId, "devices")],
    }),

    // Saved events
    getUserSavedEvents: build.query<SavedUserEvent[], number>({
      query: (userId) => `/users/${userId}/saved-events`,
      transformResponse: (response: unknown) => {
        return getTypedArrayResponse(response, isSavedUserEvent);
      },
      providesTags: (_result, _error, userId) => [userSubTag(userId, "saved-events")],
    }),
    
    saveEvent: build.mutation<void, { userId: number; eventId: number }>({
      query: ({ userId, eventId }) => ({
        url: `/users/${userId}/saved-events/${eventId}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { userId }) => [userSubTag(userId, "saved-events")],
    }),
    
    removeSavedEvent: build.mutation<void, { userId: number; eventId: number }>({
      query: ({ userId, eventId }) => ({
        url: `/users/${userId}/saved-events/${eventId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { userId }) => [userSubTag(userId, "saved-events")],
    }),

    getDeletedUsers: build.query<DeletedUser[], void>({
      query: () => ({ url: "/users/deleted" }),
      transformResponse: (response: any) => {
        const list = response?.success
          ? response?.data?.users ?? []
          : Array.isArray(response?.users)
            ? response.users
            : [];
        return list.map((row: any) => ({
          id: Number(row.id),
          preDeletionEmail: row.preDeletionEmail ?? row.pre_deletion_email ?? null,
          deletedAt: row.deletedAt ?? row.deleted_at ?? null,
          deletionReason: row.deletionReason ?? row.deletion_reason ?? null,
          deletionReasonCode: row.deletionReasonCode ?? row.deletion_reason_code ?? null,
          createDate: row.createDate ?? row.create_date ?? "",
        })) as DeletedUser[];
      },
      providesTags: [{ type: "users", id: "DELETED" }],
    }),
  }),
});

export interface DeletedUser {
  id: number;
  preDeletionEmail: string | null;
  deletedAt: string | null;
  deletionReason: string | null;
  deletionReasonCode: string | null;
  createDate: string;
}

// Export all the hooks needed by the application
export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useGetUserByEmailQuery,
  useGetUserTypesQuery,
  useRegisterUserMutation,
  useRegisterGoogleUserMutation,
  useCreateFictionalUserMutation,
  useUpdateUserMutation,
  useBanUsersMutation,
  useUnbanUsersMutation,
  useRegisterDeviceMutation,
  useGetUserDevicesQuery,
  useGetUserSavedEventsQuery,
  useSaveEventMutation,
  useRemoveSavedEventMutation,
  useGetDeletedUsersQuery,
} = usersApi;
